import * as React from 'react';
import { useState, useImperativeHandle, useContext, useEffect, useCallback } from 'react';
import classNames from 'classnames';
import pickAttrs from 'rc-util/lib/pickAttrs';
import KeyCode from 'rc-util/lib/KeyCode';
import Input from './Input';
import useLayoutEffect from '../hooks/useLayoutEffect';
import { ContactActions, TempContactActions, useActions, useAppSelector } from '@web-common/state/createStore';
import { WriteContext } from '@web-setting/Mail/components/CustomTemplate/template_add_modal';
const CustomizeRenderSelectorComponent = React.memo(({ value, content, itemDisabled, closable, onClose, chosen, index, isLast, selectionPrefixCls, tagRender }) => {
    return (React.createElement("span", { className: classNames(`${selectionPrefixCls}-item contact-tag-item`, {
            [`${selectionPrefixCls}-item-disabled`]: itemDisabled,
            'item-chosen': chosen,
        }) }, tagRender({
        label: content,
        value,
        disabled: itemDisabled,
        closable,
        onClose,
        itemIndex: index,
        isLast,
    })));
});
CustomizeRenderSelectorComponent.displayName = 'CustomizeRenderSelector';
const RenderItem = React.memo(({ disabled, maxTagTextLength, selectionPrefixCls, tagRender, value, label, index, isLast, itemDisabled = false, chosen, onSelect }) => {
    const closable = !disabled && !itemDisabled;
    const displayLabel = React.useMemo(() => {
        if (typeof maxTagTextLength === 'number') {
            if (typeof label === 'string' || typeof label === 'number') {
                const strLabel = String(label);
                if (strLabel.length > maxTagTextLength) {
                    return `${strLabel.slice(0, maxTagTextLength)}...`;
                }
            }
        }
        return label;
    }, [label, maxTagTextLength]);
    const onSelectRef = React.useRef(onSelect);
    const valueRef = React.useRef(value);
    React.useEffect(() => {
        onSelectRef.current = onSelect;
    }, [onSelect]);
    React.useEffect(() => {
        valueRef.current = value;
    }, [value]);
    const handleClose = React.useCallback((event) => {
        onSelectRef.current(valueRef.current, { selected: false });
    }, []);
    return (React.createElement(CustomizeRenderSelectorComponent, { value: value, content: displayLabel, itemDisabled: itemDisabled, closable: closable, onClose: handleClose, chosen: chosen, index: index, isLast: isLast, selectionPrefixCls: selectionPrefixCls, tagRender: tagRender }));
});
RenderItem.displayName = 'RenderItem';
const SelectSelector = React.forwardRef((props, ref) => {
    const { id, prefixCls, values, open, searchValue, inputRef, placeholder, disabled, mode, showSearch, autoFocus, autoComplete, accessibilityIndex, tabIndex, removeIcon, maxTagTextLength, tagRender, onToggleOpen, onSelect, onInputChange, onInputPaste, onInputKeyDown, onInputMouseDown, onInputCompositionStart, onInputCompositionEnd, onInputOrderChange, inputOrderExternal, } = props;
    const { isMailTemplate } = useContext(WriteContext);
    const measureRef = React.useRef(null);
    const [inputWidth, setInputWidth] = useState(0);
    const [focused, setFocused] = useState(false);
    const [inputOrder, setInputOrder] = useState(0);
    const [chosenList, setChosenList] = useState([]);
    const onContactActions = isMailTemplate ? TempContactActions : ContactActions;
    const contactActions = useActions(onContactActions);
    const { emails } = useAppSelector(state => (isMailTemplate ? state.tempContactReducer.selectedTags : state.contactReducer.selectedTags));
    const inputValue = open || mode === 'tags' ? searchValue : '';
    const inputEditable = mode === 'tags' || (showSearch && (open || focused)) || false;
    const allTags = values.map(v => { var _a, _b, _c; return (_c = (_b = (_a = v.label) === null || _a === void 0 ? void 0 : _a.props) === null || _b === void 0 ? void 0 : _b.item) === null || _c === void 0 ? void 0 : _c.email; });
    const selectType = values.map(v => { var _a, _b, _c; return (_c = (_b = (_a = v.label) === null || _a === void 0 ? void 0 : _a.props) === null || _b === void 0 ? void 0 : _b.item) === null || _c === void 0 ? void 0 : _c.mailMemberType; })[0];
    const toogleSelect = (emails, cursorMail) => (emails.includes(cursorMail) ? emails.filter(email => email !== cursorMail) : [...emails, cursorMail]);
    useEffect(() => {
        onInputOrderChange && onInputOrderChange(inputOrder);
    }, [inputOrder]);
    useEffect(() => {
        if (typeof inputOrderExternal === 'number') {
            setInputOrder(inputOrderExternal);
        }
    }, [inputOrderExternal]);
    useImperativeHandle(ref, () => ({
        onKeyDown: async (e) => {
            const { which, metaKey, shiftKey, ctrlKey } = e;
            if (which === KeyCode.LEFT) {
                if (inputOrder < values.length) {
                    setInputOrder(inputOrder + 1);
                }
                if (shiftKey) {
                    const cursorMail = allTags[values.length - 1 - inputOrder];
                    contactActions.doSelectTags({
                        emails: toogleSelect(emails, cursorMail),
                        type: selectType,
                    });
                }
                setChosenList([]);
            }
            else if (which === KeyCode.RIGHT) {
                if (inputOrder > 0) {
                    setInputOrder(inputOrder - 1);
                }
                if (shiftKey) {
                    const cursorMail = allTags[values.length - inputOrder];
                    contactActions.doSelectTags({
                        emails: toogleSelect(emails, cursorMail),
                        type: selectType,
                    });
                }
                setChosenList([]);
            }
            else if (which === KeyCode.A && (metaKey || ctrlKey)) {
                if (inputValue.length > 0)
                    return;
                contactActions.doSelectTags({
                    emails: allTags,
                    type: selectType,
                });
                setChosenList(values === null || values === void 0 ? void 0 : values.map(val => val === null || val === void 0 ? void 0 : val.value));
            }
        },
        getLastEnabledIndex: () => values.length - inputOrder - 1,
        getChosenList: () => chosenList,
        clearChosenList: () => {
            setChosenList([]);
        },
        setInputIndex: (index) => {
            setInputOrder(index);
        },
        setChosenValue: (valList) => {
            setChosenList(valList);
        },
    }));
    const selectionPrefixCls = `${prefixCls}-selection`;
    useLayoutEffect(() => {
        var _a;
        setInputWidth(((_a = measureRef === null || measureRef === void 0 ? void 0 : measureRef.current) === null || _a === void 0 ? void 0 : _a.scrollWidth) || 4);
    }, [inputValue]);
    const onSelectRef = React.useRef(onSelect);
    onSelectRef.current = onSelect;
    const handleSelect = useCallback((value, option) => {
        onSelectRef.current(value, option);
    }, []);
    const inputNode = (React.createElement("div", { className: `${selectionPrefixCls}-search`, style: { width: inputWidth }, onFocus: () => {
            setFocused(true);
        }, onBlur: () => {
            setFocused(false);
        } },
        React.createElement(Input, { ref: inputRef, open: open, prefixCls: prefixCls, id: id, inputElement: null, disabled: disabled, autoFocus: autoFocus, autoComplete: autoComplete, editable: inputEditable, accessibilityIndex: accessibilityIndex, value: inputValue, onKeyDown: onInputKeyDown, onMouseDown: onInputMouseDown, onChange: onInputChange, onPaste: onInputPaste, onCompositionStart: onInputCompositionStart, onCompositionEnd: onInputCompositionEnd, tabIndex: tabIndex, attrs: pickAttrs(props, true) }),
        React.createElement("span", { ref: measureRef, className: `${selectionPrefixCls}-search-mirror`, "aria-hidden": true },
            inputValue,
            "\u00A0")));
    const valuesLastIndex = values.length - 1;
    const selectionNode = (React.createElement(React.Fragment, null,
        values.map((value, index) => {
            const isLast = index === valuesLastIndex;
            const chosen = chosenList.indexOf(value.value) >= 0;
            const item = (React.createElement(RenderItem, { key: `${value.value}`, disabled: disabled, maxTagTextLength: maxTagTextLength, selectionPrefixCls: selectionPrefixCls, tagRender: tagRender, value: value.value, label: value.label, index: index, isLast: isLast, itemDisabled: false, chosen: chosen, onSelect: handleSelect }));
            if (inputOrder === values.length - index) {
                return (React.createElement(React.Fragment, null,
                    inputNode,
                    item));
            }
            return [React.createElement("div", { style: { width: 4 } }), item];
        }),
        inputOrder === 0 ? inputNode : null));
    return (React.createElement(React.Fragment, null,
        selectionNode,
        !values.length && !inputValue && React.createElement("span", { className: `${selectionPrefixCls}-placeholder` }, placeholder)));
});
export default SelectSelector;
